import { AnimationDriver } from 'components/memory/MemoryUtils'
import Router from 'next/router'
import { useCallback } from 'react'
import useSWR from 'swr'
import { mutateAnalyticsData } from 'utils/analytics'
import { MemoryPaywallType } from 'utils/analytics/types'
import { matchMap } from 'utils/general'

const MEMORY_PAYWALL_KEY = '@memory-paywall'

interface PaywallMemory {
  driver: AnimationDriver
  memoryUuid: string
}

function setPaywallMemory(memory: PaywallMemory | null) {
  if (memory) {
    sessionStorage.setItem(MEMORY_PAYWALL_KEY, JSON.stringify(memory))
  } else {
    sessionStorage.removeItem(MEMORY_PAYWALL_KEY)
  }
}

function fetchPaywallMemory(): PaywallMemory | null {
  const json = sessionStorage.getItem(MEMORY_PAYWALL_KEY)
  if (!json) return null
  return JSON.parse(json) ?? null
}

const livePortraitDrivers: AnimationDriver[] = ['FUN', 'NOSTALGIA']
export function determinePaywallType(memory: PaywallMemory): MemoryPaywallType {
  const isLivePortraitDriver = livePortraitDrivers.includes(memory.driver)

  return isLivePortraitDriver ? 'lp' : 'sp'
}

export function usePaywallMemory() {
  const { data: paywallMemory, mutate: mutatePaywallSwr } = useSWR<PaywallMemory | null>(
    MEMORY_PAYWALL_KEY,
    fetchPaywallMemory
  )

  const hitMemoryPaywallWithRedirect = useCallback(
    async (memory: PaywallMemory) => {
      mutateAnalyticsData({
        memory_paywall_type: determinePaywallType(memory),
        flow: determinePaywallType(memory) === 'lp' ? 'memories' : 'singing_portraits',
      })
      setPaywallMemory(memory)
      await mutatePaywallSwr(memory)
      const paywallUrl = matchMap(determinePaywallType(memory), {
        lp: '/subscription/animations',
        sp: `/memories/${memory.memoryUuid}/locked?next=/me/life-stories`,
      })
      await Router.push(paywallUrl)
    },
    [mutatePaywallSwr]
  )

  const clearPaywallMemory = useCallback(async () => {
    setPaywallMemory(null)
    await mutatePaywallSwr(null)
  }, [mutatePaywallSwr])

  return { paywallMemory, hitMemoryPaywallWithRedirect, clearPaywallMemory }
}
